// src/data/applicationRecord.js
//
// ONB-1 (PLAN ONB-1, approved by Bob 2026-09-21; rulings 193, 194, 196).
//
// THE APPLICATION IS NOT AN ACCOUNT. Ruling 193.6: applying is not joining - an application creates a
// pending applicant, never an account with a role. Nothing in this module writes a role, and the only
// writer of a role claim on this platform stays the one that already is (ONB-3 extends it; this does not).
//
// This file is PURE. No Firestore, no network, no clock. The callable in functions/ supplies the server
// time and does the writing; the browser uses the same validation so an applicant is not told at submit
// what he could have been told at the field. One source, two readers (directives section XI).

// The three blocks Bob approved 2026-09-21, unchanged from YCP_ONBOARDING_REQUIREMENTS_v4_2026-09-20.md.
// Every field names what it makes work - his test at ruling 193.5 - and that reason is carried HERE so a
// later reader cannot quietly add a field without one.
export const BLOCKS = Object.freeze({
  personal: Object.freeze([
    ['fullLegalName',  'the claimant name on every claim and letter'],
    ['mailingAddress', 'where the Organization writes to him; service of anything posted'],
    ['email',          'how he is reached, and how a claim filed for him finds him'],
    ['mobile',         'urgent contact on a running filing window'],
  ]),
  work: Object.freeze([
    // Forensic audit follow-up 2026-10-17: these said the roster was checked at approval. No roster is
    // held and approvals record the check NOT PERFORMED (functions/index.js rosterCheck). The reason now says what they do.
    ['rosterNumber',   'where he stands on the seniority roster; filled onto a claim filed for him (claimantRosterFacts.js)'],
    ['seniorityDate',  'his seniority date; filled onto a claim filed for him (claimantRosterFacts.js)'],
    // Craft BEFORE railroad (2026-09-22, section 341): the railroad list depends on the craft, as on New Claim.
    ['craftAndClass',  'resolves the wage unit that values a claim'],
    ['railroad',       'resolves the agreement that governs'],
    ['workLocation',   'resolves the territory that routes him to his Local Chairperson'],
    ['employeeNumber', 'how the carrier identifies him'],
    // Section 345 (2026-09-22), Bob: the application never asked for it, while every claim does.
    // A job symbol changes on a bid or a bump, so it is his CURRENT assignment, re-stated on each claim.
    ['jobSymbol',      'the assignment he holds: the job symbol names the position a claim is made on'],
    // Section 774 (Bob 2026-10-22 14:39 ET) made these OPTIONAL. SUPERSEDED (Bob 2026-10-25): "all applicants
    // must still enter their wages when they first sign up to the platform." Now REQUIRED. The reasoning stands
    // and is why it is HIS figure, not a sourced one: no public body publishes a yardmaster or T&E rate
    // (section 759) and no agreement's rates are confirmed for live valuation (ruling 324), so a claim's value
    // starts from his word - and every applicant must give that word at signup.
    ['payRate',        'your rate of pay as you state it - required; it is what a claim filed for you is valued at'],
    ['payBasis',       'whether that rate is per hour, per day or per week - required, given with the rate'],
  ]),
  union: Object.freeze([
    ['unionName',         'which organization holds his membership'],
    ['generalCommittee',  "which committee's record he belongs to - read from his craft and railroad, never typed (doctrine 5)"],
    ['localCommittee',    'which local approves him - read from craft x railroad x work location; blank means his General Chairperson assigns it (doctrine 5, 9)'],
    ['claimedOffice',     'ruling 193.2 - a statement by the applicant, never a grant'],
  ]),
});

// Not asked, deliberately. v4 lists these; the guard exists so the form cannot grow them by accident.
export const NEVER_ASKED = Object.freeze([
  'ssn', 'socialSecurityNumber', 'dateOfBirth', 'dob', 'bankAccount', 'routingNumber',
  'duesStatus', 'disciplineHistory', 'medical', 'medicalInformation',
]);

export const CLAIMED_OFFICE = Object.freeze(['none', 'local_chair', 'general_chair', 'other']);

// Section 774 made these optional; SUPERSEDED 2026-10-25 - wage entry is REQUIRED at signup, so nothing
// is optional now. The constant stays (empty) so requiredFields()/the form keep one list to read.
export const OPTIONAL = Object.freeze([]);
export const PAY_BASES = Object.freeze(['hourly', 'daily', 'weekly']);
// Required now (both): the rate an amount in dollars and cents, more than zero and under 100,000, with a basis.
// requiredFields() already refuses an empty rate or basis; this adds the FORMAT and the pairing check.
export function payRateProblem(form) {
  const f = form || {};
  const rate = String(f.payRate == null ? '' : f.payRate).trim().replace(/^\$/, '').replace(/,/g, '');
  const basis = String(f.payBasis == null ? '' : f.payBasis).trim();
  if (!rate && !basis) return '';   // both empty is caught by requiredFields() with the field label; no format msg here
  if (rate && !basis) return 'Choose whether your rate of pay is per hour, per day or per week.';
  if (!rate && basis) return 'Enter your rate of pay.';
  if (!PAY_BASES.includes(basis)) return 'Your rate of pay must be per hour, per day or per week.';
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(rate) || Number(rate) <= 0) return 'Enter your rate of pay as an amount, for example 38.50.';
  return '';
}

// DERIVED, NOT ASKED (2026-09-22, section 341). Bob's doctrine of record (data/LC_REPRESENTATION_DOCTRINE.md
// section 5): the Local and the General Committee are projections of craft x railroad x territory - 'the
// local number does not identify an LC' (5.2). They stay in the record, shown to him before he submits, and
// src/data/routing.js resolveApplicantRouting() fills them; the Cloud Function fills them again from its own
// answer. He is never required to type them, and what he might type is never used.
export const DERIVED = Object.freeze(['generalCommittee', 'localCommittee']);

// Every field the record carries, asked or derived.
export function allFields() {
  return [].concat(BLOCKS.personal, BLOCKS.work, BLOCKS.union).map((r) => r[0]);
}

// The fields HE must supply.
export function requiredFields() {
  return allFields().filter((k) => DERIVED.indexOf(k) === -1 && OPTIONAL.indexOf(k) === -1);
}

// Why each field is asked, by name - so a reviewer can check ruling 193.5 without reading the plan.
export function reasonFor(field) {
  const row = [].concat(BLOCKS.personal, BLOCKS.work, BLOCKS.union).find((r) => r[0] === field);
  return row ? row[1] : '';
}

export function validateApplication(form) {
  const f = form || {};
  const missing = requiredFields().filter((k) => !String(f[k] == null ? '' : f[k]).trim());
  const forbidden = NEVER_ASKED.filter((k) => k in f);
  const office = String(f.claimedOffice || '').trim();
  const badOffice = office !== '' && !CLAIMED_OFFICE.includes(office);
  const badPay = payRateProblem(f);   // section 774
  return {
    ok: missing.length === 0 && forbidden.length === 0 && !badOffice && !badPay,
    badPay,
    missing,
    forbidden,                       // a field that must never be collected was sent - refuse, never store
    badOffice: badOffice ? office : '',
  };
}

// ONB-1.4, taken under PD-19 in PLAN ONB-1 and approved with it.
//   held seat        -> that Local Chairperson
//   vacant seat      -> the General Chairperson (ruling 60: a vacant seat's work vests in the GC)
//   no scopeKey      -> the GC of the named committee, visibly unrouted
//   no GC onboarded  -> held for the administrator (ruling 193.4 puts the GC first; there is nobody
//                       below him yet, and a man who finds the platform early has done nothing wrong)
export function routeApplication({ scopeKey, generalCommittee, seatHolderUid, gcOnboarded }) {
  const cmte = String(generalCommittee || '').trim();
  if (!cmte) return { route: 'held_admin', target: 'admin', routingStatus: 'no_committee' };
  if (!gcOnboarded) return { route: 'held_admin', target: 'admin', routingStatus: 'no_gc_onboarded' };
  const key = String(scopeKey || '').trim();
  if (!key) return { route: 'gc', target: cmte, routingStatus: 'unresolved' };
  if (!seatHolderUid) return { route: 'gc', target: cmte, routingStatus: 'seat_vacant' };
  return { route: 'lc', target: key, routingStatus: 'routed' };
}

// The shape the callable writes. Server-supplied values are passed in, never invented here.
export function buildApplication({ form, scopeKey, routing, now, acknowledgements, accuracyNoticeVersion }) {
  const f = form || {};
  const out = {};
  // allFields, NOT requiredFields: the derived generalCommittee and localCommittee are stored (the read rule
  // scopes on generalCommittee). Found 2026-09-22 while making them derived - requiredFields would drop them.
  for (const k of allFields()) out[k] = String(f[k] == null ? '' : f[k]).trim();
  out.claimedOffice = out.claimedOffice || 'none';
  out.payRate = out.payRate.replace(/^\$/, '').replace(/,/g, '');   // section 774: stored as the amount he gave, '' when not answered
  out.scopeKey = String(scopeKey || '');
  out.route = routing.route;
  out.routeTarget = routing.target;
  out.routingStatus = routing.routingStatus;
  out.status = 'pending';                       // pending | approved | refused, and nothing else
  out.submittedAt = now;                        // the server clock, as every other record here
  // The tick records WHAT HE WAS SHOWN, not merely that he ticked. A tick with no text behind it says
  // nothing a later reader can check; this is ONB-5's principle arriving early, at the cost of one field.
  out.acknowledgements = acknowledgements || {};
  out.accuracyNoticeVersion = String(accuracyNoticeVersion || '');
  return out;
}
